import React from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { motion } from "framer-motion";
import { formatINR, pimg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const stock = product.stock ?? 0;
  const unavailable = !product.available || stock <= 0;
  const lowStock = stock > 0 && stock <= 5;

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.06 }}
      data-testid={`product-card-${product.id}`}
      className="group bg-white rounded-2xl overflow-hidden border border-[#EFEBE1] hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
    >
      <Link to={`/product/${product.id}`} className="block relative">
        <div className="aspect-square overflow-hidden bg-mutedcream">
          <img
            src={pimg(product.image)}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        {product.best_seller && (
          <span className="absolute top-3 left-3 bg-gold text-chocodark text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
            Best Seller
          </span>
        )}
        {unavailable && (
          <span className="absolute top-3 right-3 bg-chocodark/80 text-cream text-[10px] font-semibold uppercase px-2.5 py-1 rounded-full">
            Sold Out
          </span>
        )}
        {!unavailable && lowStock && (
          <span className="absolute top-3 right-3 bg-orange-500 text-white text-[10px] font-semibold uppercase px-2.5 py-1 rounded-full">
            Only {stock} left
          </span>
        )}
      </Link>

      <div className="p-4 sm:p-5">
        <Link to={`/product/${product.id}`}>
          <h3 className="font-display text-lg font-semibold text-choco leading-tight line-clamp-2 min-h-[2.6rem] hover:text-goldsoft transition-colors">
            {product.name}
          </h3>
        </Link>
        {product.weight && <p className="text-xs text-muted-foreground mt-1">{product.weight}</p>}
        <p className="text-sm text-muted-foreground mt-2 line-clamp-2 min-h-[2.5rem]">{product.description}</p>
        <div className="flex items-center justify-between mt-4">
          <span data-testid={`product-price-${product.id}`} className="font-display text-xl font-bold text-choco">
            {formatINR(product.price)}
          </span>
          <button
            data-testid={`add-to-cart-${product.id}`}
            disabled={unavailable}
            onClick={() => {
              addItem(product, 1);
              toast.success(`${product.name} added to cart`);
            }}
            className="inline-flex items-center gap-1.5 rounded-full bg-choco text-cream text-sm font-medium px-4 py-2.5 hover:bg-chocodark active:scale-95 transition-[background-color,transform] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus size={16} /> Add
          </button>
        </div>
      </div>
    </motion.div>
  );
};
